// apiService.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc, getFirestore } from "firebase/firestore"; import io from 'socket.io-client';
import { socketNewRow } from './socket_new_row';
import { socketEditRow, socketBigEdit, socketTableUpdate, socketDeleteRow } from './socket_edit_row';
import { config } from '../../config';
import { getOrCreateClientId, parseUserAgent, getDeviceFingerprint, getDeviceDescription } from '../utils/clientIdentifier';
import type { RFPAnalysisPayload } from '../pages/Map/rfp/rfpTypes';
import type { RFPAnalysisResult } from './tableDefinitions';
import type { CarrierRateContext } from '../carrierCostRanking';
export type { RFPAnalysisPayload, RFPAnalysisResult, CarrierRateContext };
import { useUserAnalytics } from './userAnalytics';

export interface RFPLeaseStatItem {
	lease_coord_id: string;
	lease_name: string;
	region: string | null;
	latitude: number;
	longitude: number;
	avg_monthly_bbl: number;
	avg_bsw: number;
	avg_gravity: number;
}

import {
	CrudeSchedule,
	LeaseCoordinateItem,
	StationCoordinateItem,
	PendingApprovalItem,
	WarningAdjustmentItem,
	PumperContactItem,
	ComponentsPriceItem,
	MarketPriceItem,
	NameCorrectionsItem,
	TruckingRateItem,
	ProdDiscountItem,
	CrudeScheduleMain,
	ChangelogItem,
	ComponentPriceItem,
	ApprovedMileageItem,
	ProducerPriceItem,
	CustomerReportsItem,
	MobileReqMailingListItem,
	auditTableItem,
	incidentsTableItem,
	incidentsDefinitionItem,
	NominationsItem,
	ProducerDefinitionItem,
	CarrierPricingItem,
	CarrierPricingProducerItem,
	EntityDefinitionItem,
	EntityProducerHistoryItem,
	CarrierDefinitionItem,
	NglSchedule,
	NglTicketDataItem,
	CalculatedRoutesItem,
	userGroupItem,
	CrudeMain,
	CrudeMainPickup,
	CrudeMainDelivery,
	BottomsMainItem,
	CrudeTanksDefinitionItem,
	CarrierItem,
	MainTableItem,
	VacuumSchedule,
	BottomsScheduleVsItem,
	TableDateRanges,
	LabResultsItem,
	MarketQualitySpecItem,
	TestTypeDefinitionItem,
	OpisNglPriceItem,
	TablePresetsItem,
	UserAnalyticsItem,
} from './tableDefinitions';

export interface ApiData {
	main_table: MainTableItem[];
	lease_coordinates: LeaseCoordinateItem[];
	market_coordinates: StationCoordinateItem[];
	pending_approvals: PendingApprovalItem[];
	table_presets: TablePresetsItem[];
	warning_adjustment: WarningAdjustmentItem[];
	pumper_contact: PumperContactItem[];
	components_price: ComponentsPriceItem[];
	opis_ngl_prices: OpisNglPriceItem[];
	market_discounts: MarketPriceItem[];
	name_corrections: NameCorrectionsItem[];
	trucking_rates: TruckingRateItem[];
	carrier_rate_context: CarrierRateContext | null;
	producer_discounts: ProdDiscountItem[];
	// schedule_table: CrudeSchedule[];
	bottoms_schedule_table: VacuumSchedule[];
	changelog: ChangelogItem[];
	// date_ranges: TableDateRanges[];
	components_pricing: ComponentPriceItem[];
	approved_mileage: ApprovedMileageItem[];
	producer_pricing: ProducerPriceItem[];
	customer_reports: CustomerReportsItem[];
	mobile_req_mailing_list: MobileReqMailingListItem[];
	audit_table: auditTableItem[];
	incidents_table: incidentsTableItem[];
	incidents_definition: incidentsDefinitionItem[];
	nominations: NominationsItem[];
	automate_scheduling: [];
	producer_definition: ProducerDefinitionItem[];
	entity_definition: EntityDefinitionItem[];
	entity_producer_history: EntityProducerHistoryItem[];
	carrier_definition: CarrierDefinitionItem[];
	carrier_pricing: CarrierPricingItem[];
	carrier_pricing_producer: CarrierPricingProducerItem[];
	ngl_schedule_table: NglSchedule[];
	ngl_ticket_data: NglTicketDataItem[];
	calculated_routes: CalculatedRoutesItem[];
	producer_groups: userGroupItem[];
	schedule_table: CrudeScheduleMain[];
	changelog_byid: ChangelogItem[];
	user_analytics: UserAnalyticsItem[];
	crude_main: CrudeMain[];
	crude_main_pickup: CrudeMainPickup[];
	crude_main_delivery: CrudeMainDelivery[];

	bottoms_main_table: BottomsMainItem[];
	vac_schedule_table_vs: BottomsScheduleVsItem[];
	vac_unassigned_oi_tickets: { oi_ticket_number: string; oi_service_date: string | null }[];
	crude_tanks_definition: CrudeTanksDefinitionItem[];
	oauth_clients: OAuthClient[];

	lab_results: LabResultsItem[];
	market_quality_specs: MarketQualitySpecItem[];
	test_type_definitions: TestTypeDefinitionItem[];

	carrier_cptransport: CarrierItem[];
	carrier_diamondb: CarrierItem[];
	carrier_endeavor: CarrierItem[];
	carrier_guardian: CarrierItem[];
	carrier_heartriver: CarrierItem[];
	carrier_heartrivernd: CarrierItem[];
	carrier_penley: CarrierItem[];
	carrier_southhaven: CarrierItem[];
	carrier_texon: CarrierItem[];
	carrier_opa: CarrierItem[];
	carrier_emmaus: CarrierItem[];
	carrier_troy: CarrierItem[];
}

interface UploadResult {
	status: 'success' | 'error';
	message?: string;
	rows?: number;
	columns?: number;
}

interface DbInfo {
	connection_name: string;
	instance_name: string;
	database_name: string;
	is_production: boolean;
	is_development: boolean;
}

// ── OAuth admin types (exported for ApiClientsPanel) ───────────────────────────
export interface OAuthClient {
	client_id: string;
	label: string;
	producer_id: number;
	producer_name: string;
	scopes: string[] | string;
	is_active: number;
	created_at: string;
	expires_at: string | null;
	last_used_at: string | null;
	allowed_ips: string[] | null;
}

export interface ApiLogEntry {
	id: number;
	endpoint: string;
	method: string;
	response_code: number;
	source_ip: string;
	requested_at: string;
}

interface HeartbeatStats {
	avgLatency: number;
	maxLatency: number;
	count: number;
}

interface UserInfoContextType {
	apiData: ApiData | null;
	setApiData: (data: ApiData | null) => void;
	socketStatus: boolean;
	setSocketStatus: (status: boolean) => void;
	apiHealthStatus: boolean;
	heartbeatStats: HeartbeatStats;
	triggerManualHeartbeat: () => void;
	clientIP: string | null;
	clientInfo: { id: string; browser: string; os: string; deviceDescription?: string; fingerprint?: string } | null;
	dbInfo: DbInfo | null;
	userDetails: UserDetails | null;
	setUserDetails: React.Dispatch<React.SetStateAction<UserDetails | null>>;
	allUsers: any[];
	isUsersLoading: boolean;
	fetchAllUsers: () => Promise<any[]>;
	isLoading: boolean;
	isEditLoading: boolean;
	isCarrierLoading: boolean;
	isAutomateSchedulingLoading: boolean;
	isCreatingLoading: boolean;
	getUserById: (uid: string) => Promise<any>;
	getUsersByIds: (ids: string[]) => Promise<any[]>;
	handleAddRow: (tableName: string, rowData: Record<string, any>, loadAmount?: number, request_param?: string, original_rid?: string, web_split?: string, parentId?: number) => Promise<boolean>;
	handleEditOrder: (table_name: string, id: string | number, edited_col: string, old_value: string | number, new_value: string | number | null, parent_id?: number, reason?: string, extra?: Record<string, any>) => Promise<[boolean, any?, EditResult?]>;
	handleDeleteRow: (id: Set<string | number>, table_name: string, parent_id?: number) => Promise<boolean>;
	handleGetTable: (table_name: string, parameter?: string, replaceData?: boolean) => Promise<any>;
	handleMonthRequest: (table_name: string, timeframe: string) => Promise<void>;
	TriggerDailyUpdate: (trigger: string, mode: string, param_value?: string) => Promise<void>;
	TriggerCustomerReport: (trigger: string, mailing_list: string, currentMonth: boolean, selectedMonth?: string, requestType?: string) => Promise<void>;
	handleCreateUser: (email: string, name: string, password: string, company: string, producer_id: number | null, section: string, role: string, sendWelcomeEmail: boolean, Cc?: string) => Promise<void>;
	handleDeleteUser: (uid: string) => Promise<void>;
	handleEditSequence: (table_name: string, editData: Record<any, any>) => Promise<boolean>;
	handleEditReroute: (table_name: string, editData: Record<any, any>) => Promise<boolean>;
	handleResetPassword: (uid: string, email: string) => Promise<void>;
	fetchCarrierTable: (table_name: string) => Promise<void>;
	pdfUploaded: () => Promise<void>;
	reportUploader: (file: File, carrier_name: string) => Promise<UploadResult | undefined>
	pdfScraper: () => Promise<void>;
	fetchAutomateScheduling: (table_name: string) => Promise<void>;
	handleGetChangeLog: (table_name: string, rid: string) => Promise<any>;
	handleGetChangeLogTable: (table_name: string, rid: string) => Promise<any>;
	fetchRidAudit: (rid: string) => Promise<any>;
	extractTableFromPdf: (pdfFile: File, pageNumber?: number) => Promise<any>;
	handleUploadLabPdf: (file: File) => Promise<any>;
	handleCommitLabPdf: (payload: { pots: any[] }) => Promise<any>;
	handleGetMarketContributors: (marketId: number, from?: string | null, to?: string | null) => Promise<any>;
	saveColumnMapping: (carrierName: string, mappingName: string, pdfColumns: string[], excelColumns: string[], columnMapping: Record<string, string>, notes?: string) => Promise<any>;
	getCarrierMappings: (carrierName?: string) => Promise<any>;
	checkDuplicateTicket: (ticketNumber: string) => Promise<MainTableItem | null>;
	fetchVacAttachment: (href: string) => Promise<{ blob: Blob; contentType: string } | null>;
	fetchVacAttachmentsZip: (itemId: string | number) => Promise<Blob | null>;
	processingProgress: {stage: string, status: string} | null;
	// Loads locked while another edit's reprocessing runs, keyed `${table_name}:${row_id}`
	rowLocks: Record<string, RowLock>;
	// Outcome of recent edit-triggered reprocessing jobs, by job_id
	finishedJobs: Record<string, { success: boolean; error?: string }>;
	runRFPAnalysis: (payload: RFPAnalysisPayload) => Promise<RFPAnalysisResult>;
	fetchRFPLeaseStats: (producerId: number | string) => Promise<RFPLeaseStatItem[]>;
	// OAuth admin
	fetchOAuthClients: () => Promise<OAuthClient[]>;
	createOAuthClient: (label: string, producer_id: number, scopes: string[], allowed_ips: string[] | null) => Promise<{ client_id: string; client_secret: string; label: string }>;
	toggleOAuthClient: (clientId: string, isActive: boolean) => Promise<void>;
	rotateOAuthClient: (clientId: string) => Promise<{ client_id: string; client_secret: string }>;
	updateOAuthClientIps: (clientId: string, ips: string[] | null, oldIps: string[]) => Promise<void>;
	updateOAuthClientScopes: (clientId: string, scopes: string[], oldScopes: string[]) => Promise<void>;
	fetchOAuthClientLog: (clientId: string, page: number, perPage: number) => Promise<{ data: ApiLogEntry[]; total: number; page: number; per_page: number }>;
}

export interface RowLock {
	table_name: string;
	row_id: number;
	locked_by: string | null;
	locked_by_uid?: string | null;
	reason: string | null;
}

// Details of a post_edit_row response, beyond success/failure.
// status 423: the load is locked by locked_by. status 409: the value changed (current_value).
// processing: the edit triggered reprocessing, which finishes later (processing_done socket event).
export interface EditResult {
	status: number;
	error?: string;
	locked_by?: string;
	current_value?: any;
	processing?: boolean;
	job_id?: string;
}

export interface UserDetails {
	name: string;
	email: string;
	company: string;
	role: string;
	id: string;
	table_presets?: Record<string, number | string>;
}

export const UserInfoContext = createContext<UserInfoContextType | undefined>(undefined);

export const UserInfoProvider: React.FC<{ children: React.ReactNode; user: User | null; }> = ({ children, user }) => {
	const [apiData, setApiData] = useState<ApiData | null>(null);
	const [socketStatus, setSocketStatus] = useState<boolean>(false);
	const [apiHealthStatus, setApiHealthStatus] = useState<boolean>(false);
	const [debouncedSocketStatus, setDebouncedSocketStatus] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isGetTableLoading, setIsGetTableLoading] = useState<boolean>(false);
	const [isMakeEditLoading, setIsMakeEditLoading] = useState<boolean>(false);
	const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
	const [isEditLoading, setIsEditLoading] = useState<boolean>(false);
	const [isCarrierLoading, setIsCarrierLoading] = useState<boolean>(false);
	const [isAutomateSchedulingLoading, setIsAutomateSchedulingLoading] = useState(false)
	const [isCreatingLoading, setIsCreatingLoading] = useState(false);
	const [clientIP, setClientIP] = useState<string | null>(null);
	const [clientInfo, setClientInfo] = useState<{ id: string; browser: string; os: string; deviceDescription?: string; fingerprint?: string } | null>(null);
	const [dbInfo, setDbInfo] = useState<DbInfo | null>(null);
	const [heartbeatHistory, setHeartbeatHistory] = useState<Array<{ timestamp: number; latency: number }>>([]);
	const [heartbeatStats, setHeartbeatStats] = useState<HeartbeatStats>({ avgLatency: 0, maxLatency: 0, count: 0 });
	const socketRef = React.useRef<any>(null);
	const [processingProgress, setProcessingProgress] = useState<{stage: string, status: string} | null>(null);
	const [rowLocks, setRowLocks] = useState<Record<string, RowLock>>({});
	// Can arrive before the edit's HTTP response, so cells look their job up here
	const [finishedJobs, setFinishedJobs] = useState<Record<string, { success: boolean; error?: string }>>({});
	// Edits can now run concurrently, so isEditLoading tracks a count rather than a flag
	const editsInFlightRef = React.useRef(0);
	const [allUsers, setAllUsers] = useState<any[]>([]);
	const [isUsersLoading, setIsUsersLoading] = useState<boolean>(false);

	const API_URL = config.apiUrl
	const SOCKET_URL = config.socketUrl
	const BASE_URL = API_URL.replace('/api/data', '')

	const getAnalyticsHeaders = React.useCallback(async () => {
		if (!user) return null;

		const token = await user.getIdToken();
		return { Authorization: `Bearer ${token}` };
	}, [user]);

	useUserAnalytics({
		endpoint: API_URL,
		userId: user?.uid,
		getHeaders: getAnalyticsHeaders,
	});

	useEffect(() => {

		const loadApiData = async () => {

			if (!user) {
				console.log("No user, redirecting to login page");
				if (window.location.pathname !== "/login") {
					window.location.replace("/login");
				}
				return;
			}
			setIsLoading(true);

			const token = await user.getIdToken();

			console.log(`${API_URL}`)
			try {
				console.log('getting')
				const [response] = await Promise.all([
					fetch(`${API_URL}`, {
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
							'Authorization': `Bearer ${token}`,
							'Request-Platform': 'web',
							'X-Request-Type': 'init_web_app',
						},
					}),

				]);

				if (!response.ok) {
					throw new Error(`Schedule API call failed with status: ${response.status}`);
				}

				const apiData = await response.json();
				
				setApiData(apiData);
				console.log("Init data fetched");

			} catch (error) {
				console.error('An error occurred while fetching data:', error);
				if (error instanceof Error) {
					console.error('Error message:', error.message);
					console.error('Error stack:', error.stack);
				}
			} finally {
				setIsLoading(false);
			}
		};

		loadApiData();
		// on initial load, get date_ranges for date pickers
		// handleGetTable('date_ranges');

		// WebSocket for listening to changes
		const socket = io(`${SOCKET_URL}`, {
			transports: ['polling', 'websocket'],  // Allow polling first, then upgrade to WebSocket
			reconnection: true,
			reconnectionAttempts: Infinity,  // Keep trying to reconnect
			reconnectionDelay: 1000,  // Start with 1 second
			reconnectionDelayMax: 10000,  // Max 10 seconds between attempts (exponential backoff)
			randomizationFactor: 0.5,  // Add jitter to prevent thundering herd
			timeout: 20000,  // Connection timeout before giving up on a single attempt
			secure: true,  // Force HTTPS/WSS for secure connections
			rejectUnauthorized: false,  // Allow self-signed certs if needed
			autoConnect: true,
			forceNew: false,  // Reuse existing connection when possible
		});

		socketRef.current = socket;
		let heartbeatInterval: NodeJS.Timeout;

		socket.on('connect', () => {
			setSocketStatus(true);
			console.log('✅ Socket connected');

			// Lock events may have been missed while disconnected
			fetchRowLocks();

			// Reset heartbeat stats on new connection
			setHeartbeatHistory([]);
			setHeartbeatStats({ avgLatency: 0, maxLatency: 0, count: 0 });

			// Send device fingerprint to server for tracking (async)
			getDeviceFingerprint().then(deviceFingerprint => {
				socket.emit('device_info', deviceFingerprint);
			});

			// Send immediate test heartbeat
			socket.emit('heartbeat', { timestamp: Date.now() });

			// Start application-level heartbeat
			clearInterval(heartbeatInterval);
			heartbeatInterval = setInterval(() => {
				if (socket.connected) {
					socket.emit('heartbeat', { timestamp: Date.now() });
				}
			}, 30000);  // Send heartbeat every 30 seconds
		});

		socket.on('disconnect', (reason) => {
			setSocketStatus(false);
			console.warn('❌ Socket disconnected:', reason);
			clearInterval(heartbeatInterval);

			// If server disconnected us, try to reconnect immediately
			if (reason === 'io server disconnect') {
				socket.connect();
			}
		});

		socket.on('connect_error', (error) => {
			console.error('Socket connection error:', error);
		});

		socket.on('reconnect', (attemptNumber) => {
			console.log(`🔄 Reconnected after ${attemptNumber} attempt(s)`);
		});

		socket.on('reconnect_attempt', (attemptNumber) => {
			if (attemptNumber % 5 === 0) {  // Log every 5th attempt to reduce noise
				console.log(`🔄 Reconnection attempt ${attemptNumber}...`);
			}
		});

		socket.on('reconnect_error', (error) => {
			// Only log first reconnection error to reduce noise
			if ((error as any).attempt === 1) {
				console.error('Reconnection error:', error);
			}
		});

		socket.on('reconnect_failed', () => {
			console.error('❌ Reconnection failed after all attempts');
		});

		socket.on('heartbeat_ack', (data) => {
			const latency = Date.now() - data.timestamp;
			const now = Date.now();
			
			// Keep only last 10 minutes of heartbeat data
			const tenMinutesAgo = now - (10 * 60 * 1000);
			setHeartbeatHistory(prev => {
				const filtered = prev.filter(h => h.timestamp > tenMinutesAgo);
				return [...filtered, { timestamp: now, latency }];
			});
			
			// Calculate stats from history
			setHeartbeatHistory(current => {
				if (current.length === 0) {
					setHeartbeatStats({ avgLatency: 0, maxLatency: 0, count: 0 });
				} else {
					const latencies = current.map(h => h.latency);
					const avg = Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length);
					const max = Math.max(...latencies);
					setHeartbeatStats({ avgLatency: avg, maxLatency: max, count: current.length });
				}
				return current;
			});
			
			// Only log if latency is high or every 5 minutes for debugging
			if (latency > 1000) {
				console.warn(`⚠️ High heartbeat latency: ${latency}ms`);
			}
		});

		socket.on('client_ip', (data) => {
			if (data && data.ip) {
				setClientIP(data.ip);

				// Get device fingerprint (async)
				getDeviceFingerprint().then(deviceFingerprint => {
					// Parse user agent for browser/OS info
					const { browser, os } = data.userAgent ? parseUserAgent(data.userAgent) : { browser: 'Unknown', os: 'Unknown' };

					// Create readable device description
					const deviceDesc = getDeviceDescription(deviceFingerprint);

					setClientInfo({
						id: deviceFingerprint.clientId,
						browser,
						os,
						deviceDescription: deviceDesc,
						fingerprint: deviceFingerprint.browserFingerprint
					});
				});
			}
		});

		socket.on('new_row', (data) => {
			console.log('new row received')
			socketNewRow(data, setApiData);
		});

		socket.on('edit_row', (data) => {
			console.log('edit socket!')
			socketEditRow(data, setApiData);
		});

		socket.on('big_edit_data', (data) => {
			console.log('BIG edit socket!')
			socketBigEdit(data, setApiData);
		});

		socket.on('table_update', (data) => {
			console.log('table update socket!')
			if (data.order_table === 'oauth_clients') {
				// Full replace — oauth_clients uses client_id (not id) as PK
				// so row-level merge in socketTableUpdate would not match correctly.
				const parsed = (data.order_data as OAuthClient[]).map((c) => ({
					...c,
					allowed_ips: typeof c.allowed_ips === 'string'
						? (() => { try { return JSON.parse(c.allowed_ips as unknown as string); } catch { return null; } })()
						: c.allowed_ips,
				}));
				setApiData(prev => prev ? { ...prev, oauth_clients: parsed } : null);
			} else {
				socketTableUpdate(data, setApiData);
			}
		});

		socket.on('delete_row', (data) => {
			console.log('delete row socket!')
			socketDeleteRow(data, setApiData);
		});

		socket.on('pricing_context_refresh', (data: CarrierRateContext) => {
			// A singleton object, not a row set — direct replace, same as oauth_clients
			// above, rather than the row-array merge-by-id socketTableUpdate does.
			console.log('pricing context refresh socket!')
			setApiData(prev => prev ? { ...prev, carrier_rate_context: data } : prev);
		});

		socket.on('pdfs_uploaded', (data) => {
			console.log('pdfs uploaded!')
		});

		// Listen for API health status from socket server
		socket.on('api_health_status', (data: { healthy: boolean }) => {
			setApiHealthStatus(data.healthy);
		});

		socket.on('row_lock', (data: RowLock) => {
			const key = `${data.table_name}:${data.row_id}`;
			setRowLocks(prev => {
				const next = { ...prev };
				if (data.locked_by) next[key] = data;
				else delete next[key];
				return next;
			});
		});

		socket.on('processing_done', (data: { job_id: string; table_name: string; row_id: number; success: boolean; error?: string }) => {
			setFinishedJobs(prev => {
				// keep only recent jobs
				const recent = Object.entries(prev).slice(-200);
				return { ...Object.fromEntries(recent), [data.job_id]: { success: data.success, error: data.error } };
			});
		});

		socket.on('processing_progress', (data) => {
			setProcessingProgress(data);
			// Auto-clear after completion or error
			if (data.status === 'success' || data.status === 'error') {
				setTimeout(() => {
					setProcessingProgress(null);
				}, 5000); // Clear after 5 seconds
			}
		});

		// Clean up function
		return () => {
			clearInterval(heartbeatInterval);
			socket.disconnect();
			socketRef.current = null;
		};
	}, [user]);

	// Manual heartbeat trigger for testing connection
	const triggerManualHeartbeat = React.useCallback(() => {
		if (socketRef.current?.connected) {
			socketRef.current.emit('heartbeat', { timestamp: Date.now() });
			console.log('📤 Manual heartbeat triggered');
		} else {
			console.warn('⚠️ Socket not connected, cannot send heartbeat');
		}
	}, []);

	// Debounce socket status to prevent rapid UI changes
	// and does not provide a buffer.
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSocketStatus(socketStatus);
		}, 2000);

		return () => clearTimeout(timer);
	}, [socketStatus]);

	useEffect(() => {
		const fetchUserDetails = async () => {
			if (!user) return;

			const db = getFirestore();
			const userDocRef = doc(db, 'users', user.uid);

			try {
				const userDocSnap = await getDoc(userDocRef);

				if (userDocSnap.exists()) {
					setUserDetails({
					    ...userDocSnap.data(),
					    id: userDocSnap.id,
					} as UserDetails);
				} else {
					console.log('No user document found!');
				}
			} catch (error) {
				console.error('An error occurred while fetching user details:', error);
			}
		};

		fetchUserDetails();
	}, [user]);

	// Fetch database connection info
	useEffect(() => {
		const fetchDbInfo = async () => {
			if (!user) {
				// Clear DB info when there is no authenticated user to avoid stale metadata
				setDbInfo(null);
				return;
			}

			try {
				const token = await user.getIdToken();
				// Build the DB info endpoint in a way that is robust to changes in API_URL
				let dbInfoUrl: string;
				if (API_URL.includes('/api/data')) {
					dbInfoUrl = API_URL.replace('/api/data', '/api/db-info');
				} else {
					dbInfoUrl = API_URL.endsWith('/')
						? `${API_URL}db-info`
						: `${API_URL}/db-info`;
				}
				const response = await fetch(dbInfoUrl, {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${token}`,
					},
				});

				if (response.ok) {
					const data = await response.json();
					if (data.success) {
						setDbInfo({
							connection_name: data.connection_name,
							instance_name: data.instance_name,
							database_name: data.database_name,
							is_production: data.is_production,
							is_development: data.is_development,
						});
					}
				} else {
					console.error('Failed to fetch DB info:', response.status);
				}
			} catch (error) {
				console.error('Error fetching DB info:', error);
			}
		};

		fetchDbInfo();
	}, [user, API_URL]);

	function chunkArray<T>(array: T[], size = 10): T[][] {
		const chunks: T[][] = [];
		for (let i = 0; i < array.length; i += size) {
			chunks.push(array.slice(i, i + size));
		}
		return chunks;
	};

	const getUsersByIds = async (ids: string[]) => {
		if (!ids.length) return [];

		const batches = chunkArray(ids, 10);
		let results = [];
		const db = getFirestore();

		for (const batch of batches) {
			try {

				console.log('Fetching user batch:', batch);
				const q = query(
					collection(db, "users"),
					where("__name__", "in", batch)
				);

				const snap = await getDocs(q);

				results.push(
					...snap.docs.map(doc => ({
						id: doc.id,
						...(doc.data())
					}))
				);
			}
			catch (error) {
				console.error('An error occurred while fetching user data:', error);
			}
		}
		return results;
	};

	const getUserById = async (uid: string): Promise<any> => {
		if (!user) {
			console.log("No user, cannot make API call");
			return false;
		}
		try {

			const db = getFirestore();
			const userDocRef = doc(db, 'users', uid);
			const userDocSnap = await getDoc(userDocRef);
			return userDocSnap.data();
		} catch (error) {
			console.error('An error occurred while fetching data:', error);
			if (error instanceof Error) {
				console.error('Error message:', error.message);
				console.error('Error stack:', error.stack);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const fetchAllUsers = async (): Promise<any[]> => {
		console.log("Fetching all users");
		if (!user) {
			console.log("No user, cannot fetch users");
			setAllUsers([]);
			return [];
		}

		try {
			setIsUsersLoading(true);

			const db = getFirestore();
			const snapshot = await getDocs(collection(db, 'users'));

			const usersList = snapshot.docs.map(doc => ({
				uid: doc.id,
				...doc.data(),
			}));

			usersList.sort((a: any, b: any) => {
				const companyCompare = (a.company || '').localeCompare(b.company || '');
				if (companyCompare !== 0) return companyCompare;
				return (a.name || '').localeCompare(b.name || '');
			});

			setAllUsers(usersList);
			return usersList;
		} catch (error) {
			console.error('An error occurred while fetching all users:', error);
			return [];
		} finally {
			setIsUsersLoading(false);
		}
	};

	const handleAddRow = async (tableName: string, rowData: Record<string, any>, loadAmount?: number, request_param?: string, original_rid?: string, web_split?: string) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return false;
		}

		let orderData = {
			tableName: tableName,
			rowData: rowData,
			loadAmount: loadAmount,
			createdBy: userDetails!.name,
			ip_address: clientIP,
			device_id: clientInfo?.fingerprint,
		};

		if (!request_param) {
			request_param = 'default'
		}

		if (!original_rid) {
			original_rid = 'default'
		}

		try {
			const token = await user.getIdToken();

			let response;
			setIsCreatingLoading(true)

			if (web_split) {
				response = await fetch(`${API_URL}`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${token}`,
						'Request-Platform': 'web',
						'X-Request-Type': 'post_add_row',
						'Request-Param': request_param,
						'Request-Value': original_rid,
						// 'Request-Platform': web_split,
					},
					body: JSON.stringify(orderData)
				});
			} else {
				response = await fetch(`${API_URL}`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${token}`,
						'Request-Platform': 'web',
						'X-Request-Type': 'post_add_row',
						'Request-Param': request_param,
						'Request-Value': original_rid,
					},
					body: JSON.stringify(orderData)
				});
			}
			setIsCreatingLoading(false)

			if (!response.ok) {
				console.error('New order API call failed with status:', response.status);
				return false;
			}
			const data = await response.json();
			return true;

		} catch (error) {
			setIsCreatingLoading(false)
			console.error('An error occurred while creating a new order:', error);
			return false;
		}
	};

	const handleDeleteRow = async (id: Set<string | number>, table_name: string, parent_id?: number) => {

		if (!user) {
			console.log("No user, cannot make API call");
			return false;
		}

		const orderData = {
			id: Array.from(id), // Convert the Set to an Array
			table_name: table_name,
			createdBy: userDetails!.name,
			parentId: parent_id,
			ip_address: clientIP,
			device_id: clientInfo?.fingerprint,
		};

		console.log("deleted row: ", orderData)

		try {
			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'post_delete_row',
				},
				body: JSON.stringify(orderData)
			});

			if (!response.ok) {
				throw new Error(`Delete orders API call failed with status: ${response.status}`);
			}

			const data = await response.json();
			console.log("Orders deleted:", data);
			return true;

		} catch (error) {
			console.error('An error occurred while deleting orders:', error);
			return false;
		}
	};

	const fetchRowLocks = async () => {
		if (!user) return;
		try {
			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'get_row_locks',
				},
			});
			if (!response.ok) return;
			const locks: RowLock[] = await response.json();
			setRowLocks(Object.fromEntries(locks.map(lock => [`${lock.table_name}:${lock.row_id}`, lock])));
		} catch (error) {
			console.error('Failed to fetch row locks:', error);
		}
	};

	const setEditInFlight = (delta: number) => {
		editsInFlightRef.current += delta;
		setIsEditLoading(editsInFlightRef.current > 0);
	};

	const handleEditOrder = async (table_name: string, id: string | number, edited_col: string, old_value: string | number, new_value: string | number | null, parent_id?: number, reason?: string, extra?: Record<string, any>): Promise<[boolean, any?, EditResult?]> => {
		if (!user) {
			console.log("No user, cannot make API call");
			return [false, undefined];
		}

		console.log('handleEditOrder ')

		const reviewed_by = userDetails!.name

		// Strip spaces from new_value if it's a string
		const trimmedValue = typeof new_value === 'string' ? new_value.trim() : new_value;

		// Set to null if empty after trimming
		const processedValue = trimmedValue === '' ? null : trimmedValue;

		// `extra` spreads FIRST — any per-edit metadata a caller passes (e.g.
		// eligible_carrier_ids) can add fields, but must never be able to silently
		// overwrite the core edit-identity fields below just by choosing a colliding
		// key name. The explicit fields after the spread always win.
		const editData = {
			...(extra || {}),
			table_name,
			id,
			edited_col,
			old_value,
			new_value: processedValue,
			reviewed_by,
			ip_address: clientIP,
			device_id: clientInfo?.fingerprint,
			parent_id,
			reason,
		};

		console.log('sending order:', editData)

		setEditInFlight(1);
		try {
			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'post_edit_row',
				},
				body: JSON.stringify(editData)
			});

			const data = await response.json();
			const result: EditResult = { status: response.status, ...data };
			
			if (!response.ok || !data.success) {
				console.log("Response data:", data);
				console.log("Response status:", response.status);
				console.log("Response ok:", response.ok);
				console.log("Data.success:", data.success);
				console.error("Edit failed:", data.error || `HTTP ${response.status}`);
				return [false, data.error, result];
			}
			
			console.log("Edit sent successfully:", data);
			return [true, response, result];

		} catch (error) {
			console.error('An error occurred while editing orders:', error);
			return [false, undefined];
		} finally {
			setEditInFlight(-1);
		}
	};

	const handleGetTable = async (table_name: string, parameter?: string, replaceData: boolean = false): Promise<any> => {
		if (!user) {
			console.log("No user, cannot make API call");
			return null;
		}
		console.log(`Fetching data for ${table_name}${parameter ? ` with parameter: ${parameter}` : ''} (replaceData: ${replaceData})`);
		setIsGetTableLoading(true);

		try {
			const token = await user.getIdToken();

			const response = await fetch(`${API_URL}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'get_table_default',
					'Table-Name': `${table_name}`,
					'Request-Param': `${parameter || 'default'}`,
				},
			});

			if (!response.ok) {
				throw new Error(`Table request API call failed with status: ${response.status}`);
			}

			const newData = await response.json();
			console.log(`Successfully fetched data for ${table_name}${parameter ? ` (${parameter})` : ''}`);

			// Normalize date_ranges structure - handle both array and object formats
			let processedData = newData;
			if (table_name === 'date_ranges') {
				if (Array.isArray(newData) && newData.length > 0 && newData[0].date_ranges) {
					// Backend returns [{ date_ranges: {...} }], normalize to just {...}
					processedData = newData[0].date_ranges;
				}
			}

			// Initialize apiData if null, or update existing data
			setApiData(prevData => {
				// If prevData is null, initialize with empty ApiData structure
				if (!prevData) {
					return {
						[table_name]: processedData
					} as ApiData;
				}

				// Get current table data
				const currentTableData = prevData[table_name as keyof ApiData];

				// If replaceData is true, always replace the table data
				if (replaceData) {
					const currentLen = Array.isArray(currentTableData) ? currentTableData.length : 'n/a';
					const nextLen = Array.isArray(processedData) ? processedData.length : 'n/a';
					console.log(`Replacing ${table_name} data entirely ${currentLen} → ${nextLen} rows`);
					return {
						...prevData,
						[table_name]: processedData
					};
				}

				// If no current data exists or it's not an array, replace entirely
				if (!Array.isArray(currentTableData) || !Array.isArray(processedData)) {
					return {
						...prevData,
						[table_name]: processedData
					};
				}

				// Create a Map of existing data keyed by ID for efficient lookups
				const existingDataMap = new Map();
				currentTableData.forEach(item => {
					if ((item as any).id) {
						existingDataMap.set((item as any).id, item);
					}
				});

				// Process new data: replace existing rows or add new ones
				processedData.forEach(newItem => {
					if ((newItem as any).id) {
						existingDataMap.set((newItem as any).id, newItem);
					}
				});

				// Convert the Map back to an array
				const updatedData = Array.from(existingDataMap.values());

				console.log(`Updated ${table_name}: ${currentTableData.length} → ${updatedData.length} rows`);

				return {
					...prevData,
					[table_name]: updatedData
				};
			});

			return processedData;

		} catch (error) {
			console.error('An error occurred while fetching month data:', error);
			// Explicit null (not the default undefined) so callers can distinguish
			// "fetch failed" from "table is genuinely empty" ([]).
			return null;
		} finally {
			setIsGetTableLoading(false);
		}
	};
		
	const handleGetChangeLog = async (table_name: string, rid: string) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}
		console.log(`Fetching changelog for RID: ${rid}`);
		setIsGetTableLoading(true);

		try {
			const token = await user.getIdToken();

			const response = await fetch(`${API_URL}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'get_changelog_by_rid',
					'Table-Name': `${table_name}`,
					'Request-Param': `${rid}`,
				},

			});

			if (!response.ok) {
				throw new Error(`Changelog RID request failed with status: ${response.status}`);
			}

			const newData = await response.json();
			console.log(`Successfully fetched changelog data for RID: ${rid} (${newData.length} records)`);

			return newData;

		} catch (error) {
			console.error('An error occurred while fetching changelog by RID:', error);
			throw error;
		} finally {
			setIsGetTableLoading(false);
		}
	};


	const fetchRidAudit = async (rid: string) => {
		if (!user) {
			console.log('No user, cannot make API call');
			return;
		}
		try {
			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'get_rid_audit',
					'Request-Param': rid,
				},
			});
			if (!response.ok) {
				throw new Error(`RID audit request failed with status: ${response.status}`);
			}
			return await response.json();
		} catch (error) {
			console.error('An error occurred while fetching RID audit:', error);
			throw error;
		}
	};

	const handleGetChangeLogTable = async (table_name: string, parameter?: string, replaceData: boolean = false) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}

		setIsGetTableLoading(true);

		try {
			const token = await user.getIdToken();

			const response = await fetch(`${API_URL}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'get_table_default',
					'Table-Name': `${table_name}`,
					'Request-Param': `${parameter || 'default'}`,
				},
			});

			if (!response.ok) {
				throw new Error(`Table request API call failed with status: ${response.status}`);
			}

			const newData = await response.json();
			console.log(`Successfully fetched data for ${table_name}${parameter ? ` (${parameter})` : ''}`);

			let processedData = newData;

			// Initialize apiData if null, or update existing data
			setApiData(prevData => {
				// If prevData is null, initialize with empty ApiData structure
				if (!prevData) {
					return {
						[table_name]: processedData
					} as ApiData;
				}

				// Get current table data
				const currentTableData = prevData[table_name as keyof ApiData];

				// If replaceData is true, always replace the table data
				if (replaceData) {
					const currentLen = Array.isArray(currentTableData) ? currentTableData.length : 0;
					const nextLen = Array.isArray(processedData) ? processedData.length : 'n/a';
					console.log(`Replacing ${table_name} data entirely ${currentLen} → ${nextLen} rows`);
					return {
						...prevData,
						[table_name]: processedData
					};
				}

				// If no current data exists or it's not an array, replace entirely
				if (!Array.isArray(currentTableData) || !Array.isArray(processedData)) {
					return {
						...prevData,
						[table_name]: processedData
					};
				}

				// Create a Map of existing data keyed by ID for efficient lookups
				const existingDataMap = new Map();
				currentTableData.forEach(item => {
					if ((item as any).id) {
						existingDataMap.set((item as any).id, item);
					}
				});

				// Process new data: replace existing rows or add new ones
				processedData.forEach(newItem => {
					if ((newItem as any).id) {
						existingDataMap.set((newItem as any).id, newItem);
					}
				});

				// Convert the Map back to an array
				const updatedData = Array.from(existingDataMap.values());

				console.log(`Updated ${table_name}: ${currentTableData.length} → ${updatedData.length} rows`);

				return {
					...prevData,
					[table_name]: updatedData
				};
			});

		} catch (error) {
			console.error('An error occurred while fetching month data:', error);
		} finally {
			setIsGetTableLoading(false);
		}
	};

	const handleEditSequence = async (table_name: string, editData: Record<any, any>): Promise<boolean> => {
		if (!user) {
			console.log("No user, cannot make API call");
			return false;
		}

		setIsMakeEditLoading(true);

		try {
			const token = await user.getIdToken();

			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'edit_sequence',
					'Table-Name': `${table_name}`,
				},
				body: JSON.stringify(editData)
			});

			if (!response.ok) {
				console.error(`Table request API call failed with status: ${response.status}`);
				return false;
			}

			const data = await response.json();

			console.log(`Successfully made edit to ${table_name}`, data);
			// Check if the response indicates success (adjust based on your API response format)
			if (data.success !== false) { // Assumes success unless explicitly false
				return true;
			} else {
				console.error('API returned failure:', data.error || 'Unknown error');
				return false;
			}

		} catch (error) {
			console.error('An error occurred while fetching month data:', error);
			return false;
		} finally {
			setIsMakeEditLoading(false);
		}
	};

	const handleEditReroute = async (table_name: string, editData: Record<any, any>): Promise<boolean> => {
		if (!user) {
			console.log("No user, cannot make API call");
			return false;
		}

		setIsMakeEditLoading(true);

		try {
			const token = await user.getIdToken();

			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'edit_reroute',
					'Table-Name': `${table_name}`,
				},
				body: JSON.stringify(editData)
			});

			if (!response.ok) {
				console.error(`Table request API call failed with status: ${response.status}`);
				return false;
			}

			const data = await response.json();

			console.log(`Successfully made edit to ${table_name}`, data);
			// Check if the response indicates success (adjust based on your API response format)
			if (data.success !== false) { // Assumes success unless explicitly false
				return true;
			} else {
				console.error('API returned failure:', data.error || 'Unknown error');
				return false;
			}

		} catch (error) {
			console.error('An error occurred while fetching month data:', error);
			return false;
		} finally {
			setIsMakeEditLoading(false);
		}
	};

	const handleMonthRequest = async (table_name: string, timeframe: string) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}

		console.log(timeframe);
		setIsLoading(true);

		try {
			const token = await user.getIdToken();

			const response = await fetch(`${API_URL}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'get_table',
					'Table-Name': `${table_name}`,
					'Time-Period': `${timeframe}`,
				},
			});

			if (!response.ok) {
				throw new Error(`Month request API call failed with status: ${response.status}`);
			}

			const newData = await response.json();

			// Update apiData with the new records
			setApiData(prevData => {
				if (!prevData) return prevData;

				// Get the current table data
				const currentTableData = prevData[table_name as keyof ApiData];

				if (Array.isArray(currentTableData) && Array.isArray(newData)) {
					// Create a Set of existing IDs for efficient lookup
					const tableArray = currentTableData as any[];
					const itemsWithId = tableArray.filter(item => item.id);
					const existingIds = new Set(itemsWithId.map(item => item.id));

					// Filter out any duplicate records from the new data
					const uniqueNewData = (newData as any[])
						.filter(item => item.id && !existingIds.has(item.id));

					// Create a new object with the merged data
					return {
						...prevData,
						[table_name]: [...currentTableData, ...uniqueNewData]
					};
				}

				return prevData;
			});

			console.log(`Successfully added ${timeframe} data to ${table_name}`);
		} catch (error) {
			console.error('An error occurred while fetching month data:', error);
			if (error instanceof Error) {
				console.error('Error message:', error.message);
				console.error('Error stack:', error.stack);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleCreateUser = async (email: string, name: string, password: string, company: string, producer_id: number | null, section: string, role: string, sendWelcomeEmail: boolean, Cc?: string) => {
		if (!user) {
			console.log("No user, cannot create user");
			return;
		}

		console.log('handleCreateUser')

		const newUserData = {
			email,
			name,
			password,
			company,
			producer_id,
			section,
			role,
			Cc,
			sendWelcomeEmail,
		};

		try {
			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'create_user',
				},
				body: JSON.stringify(newUserData)
			});

			if (!response.ok) {
				throw new Error(`handleCreateUser failed with status: ${response.status}`);
			}
			console.log(response)

		} catch (error) {
			console.error('An error occurred while deleting orders:', error);
		}
	};

	const handleDeleteUser = async (uid: string) => {
		if (!user) {
			console.log("No user, cannot create user");
			return;
		}

		console.log('handleDeleteUser')

		const deleteUser = {
			uid,
		};

		try {
			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'delete_user',
				},
				body: JSON.stringify(deleteUser)
			});

			if (!response.ok) {
				throw new Error(`handleDeleteUser failed with status: ${response.status}`);
			}
			console.log(response)

		} catch (error) {
			console.error('An error occurred while deleting orders:', error);
		}
	};

	const handleResetPassword = async (uid: string, email: string) => {
		if (!user) {
			console.log("No user, cannot create user");
			return;
		}

		console.log('handleResetPassword')

		const passReset = {
			uid,
			email,
		};

		try {
			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'reset_password',
				},
				body: JSON.stringify(passReset)
			});

			if (!response.ok) {
				throw new Error(`handleResetPassword failed with status: ${response.status}`);
			}
			console.log(response)

		} catch (error) {
			console.error('An error occurred while resetting password:', error);
		}
	};

	const TriggerDailyUpdate = async (trigger: string, mode: string, param_value?: string) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}

		console.log('TriggerDailyUpdate:', mode, param_value)

		setProcessingProgress(null);

		try {
			setIsEditLoading(true)

			const token = await user.getIdToken();

			let response;

			if (param_value) {
				response = await fetch(`${API_URL}`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${token}`,
						'Request-Platform': 'web',
						'X-Request-Type': trigger,
						'Request-Param': mode,
						'Request-Value': param_value,
					},
				});
			} else {
				response = await fetch(`${API_URL}`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${token}`,
						'Request-Platform': 'web',
						'X-Request-Type': trigger,
						'Request-Param': mode,
					},
				});
			}

			if (!response.ok) {
				throw new Error(`TriggerDailyUpdate failed with status: ${response.status}`);
			}

			setIsEditLoading(false)
			console.log(response)

		} catch (error) {
			console.error('An error occurred while deleting orders:', error);
			setIsEditLoading(false)
		}
	};

	const TriggerCustomerReport = async (producer: string, mailing_list: string, currentMonth: boolean, selectedMonth?: string, requestType: string = 'daily_report') => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}

		try {
			setIsEditLoading(true)

			const requestParams = [mailing_list, currentMonth, selectedMonth || ''];

			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': requestType,
					'Request-Param': producer,
					'Request-Value': JSON.stringify(requestParams),
					'Request-User': userDetails!.email,
				},
			});

			if (!response.ok) {
				throw new Error(`TriggerDailyUpdate failed with status: ${response.status}`);
			}

			setIsEditLoading(false)
			console.log(response)

		} catch (error) {
			console.error('An error occurred while sending customer report:', error);
		}
	};

	const fetchCarrierTable = async (table_name: string) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}

		console.log('fetchCarrierTable:', table_name)

		try {
			setIsCarrierLoading(true)

			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'get_carrier_table',
					'Request-Param': table_name,
				},
			});

			if (!response.ok) {
				throw new Error(`fetchCarrierTable failed with status: ${response.status}`);
			}

			const newData = await response.json();

			// Update the apiData state with the new carrier data
			setApiData(prevData => {
				if (!prevData) return prevData;

				return {
					...prevData,
					[table_name]: newData
				};
			});

			setIsCarrierLoading(false)

		} catch (error) {
			console.error('An error occurred while fetching carrier table', error);
		}
	};

	const fetchAutomateScheduling = async (table_name: string) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}

		try {
			setIsAutomateSchedulingLoading(true)

			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'get_automate_scheduling_table',
					'Request-Param': table_name,
				},
			});

			if (!response.ok) {
				throw new Error(`fetchCarrierTable failed with status: ${response.status}`);
			}

			const newData = await response.json();

			console.log('newData', newData, table_name)

			// Update the apiData state with the new carrier data
			setApiData(prevData => {
				if (!prevData) return prevData;
				console.log('prevData', prevData, table_name)

				return {
					...prevData,
					automate_scheduling: newData

				};
			});

			setIsAutomateSchedulingLoading(false)

		} catch (error) {
			console.error('An error occurred while fetching Automate Scheduling table', error);
			setIsLoading(false);
		}
	};

	const pdfUploaded = async () => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}

		console.log('pdf uploaded')

		try {
			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'pdf_uploaded',
				},
			});

			if (!response.ok) {
				throw new Error(`pdf upload call failed with status: ${response.status}`);
			}

		} catch (error) {
			console.error('An error occurred while sending pdf upload call', error);
		}
	};

	// OCR API Methods
	const extractTableFromPdf = async (pdfFile: File, pageNumber: number = 0) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}

		try {
			const token = await user.getIdToken();
			const formData = new FormData();
			formData.append('pdf', pdfFile);
			formData.append('page_number', pageNumber.toString());

			// Use base URL without /api/data since OCR endpoints are at /api/ocr/*
			const response = await fetch(`${BASE_URL}/api/ocr/extract-table`, {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${token}`,
				},
				body: formData
			});

			if (!response.ok) {
				throw new Error(`Extract table failed with status: ${response.status}`);
			}

			const result = await response.json();
			return result;

		} catch (error) {
			console.error('Error extracting table from PDF:', error);
			return { success: false, message: 'Error extracting table from PDF' };
		}
	};

	const handleUploadLabPdf = async (file: File) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return { success: false, message: 'Not authenticated' };
		}

		try {
			const token = await user.getIdToken();
			const formData = new FormData();
			formData.append('pdf', file);

			const baseUrl = config.apiUrl.replace('/api/data', '');

			const controller = new AbortController();
			// Ceiling, not a fixed wait — most extractions finish in 10–30s. The server
			// budget is 300s, so this only aborts a genuinely
			// stuck request; bump it if real multi-page OCR scans start getting cut off.
			const timeoutId = setTimeout(() => controller.abort(), 90000);

			const response = await fetch(`${baseUrl}/api/ocr/lab-report`, {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${token}`,
				},
				body: formData,
				signal: controller.signal,
			});

			clearTimeout(timeoutId);

			if (!response.ok) {
				// A too-large file is rejected before reaching the handler: Cloud Run
				// returns a 413 with an opaque HTML/text body (not our JSON shape), so
				// detect it by status and surface a clean message instead.
				if (response.status === 413) {
					return { success: false, message: 'The PDF is too large to upload (max 20 MB). Please use a smaller file.' };
				}
				const errText = await response.text().catch(() => '');
				throw new Error(`Lab PDF upload failed (${response.status}): ${errText}`);
			}

			return await response.json();
		} catch (error: any) {
			console.error('Error uploading lab PDF:', error);
			if (error?.name === 'AbortError') {
				return { success: false, message: 'Upload timed out. The PDF took too long to process.' };
			}
			return { success: false, message: error?.message || 'Error uploading lab PDF' };
		}
	};

	const handleCommitLabPdf = async (payload: { pots: any[] }) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return { success: false, message: 'Not authenticated' };
		}

		try {
			const token = await user.getIdToken();
			const baseUrl = config.apiUrl.replace('/api/data', '');

			// Identity is stamped here — the last step before the request — rather than
			// by the caller, so no call site can omit or override it (same pattern as
			// handleAddRow / handleDeleteRow / handleEditOrder).
			const body = {
				...payload,
				createdBy: userDetails?.name || 'unknown',
				ip_address: clientIP,
				device_id: clientInfo?.fingerprint,
			};

			const response = await fetch(`${baseUrl}/api/ocr/lab-report/commit`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				const errText = await response.text().catch(() => '');
				throw new Error(`Lab PDF commit failed (${response.status}): ${errText}`);
			}

			return await response.json();
		} catch (error: any) {
			console.error('Error committing lab PDF rows:', error);
			return { success: false, message: error?.message || 'Error committing lab PDF rows' };
		}
	};

	// Leases that delivered crude to a given market (market_coordinates.id), within
	// an optional date range. Powers the "contributing leases" panel in Lab Results.
	const handleGetMarketContributors = async (marketId: number, from?: string | null, to?: string | null) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return { success: false, error: 'Not authenticated' };
		}

		try {
			const token = await user.getIdToken();
			const baseUrl = config.apiUrl.replace('/api/data', '');
			const params = new URLSearchParams({ market_id: String(marketId) });
			if (from) params.append('from', from);
			if (to) params.append('to', to);

			const response = await fetch(`${baseUrl}/api/lab-results/market-contributors?${params.toString()}`, {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${token}`,
				},
			});

			if (!response.ok) {
				const errText = await response.text().catch(() => '');
				throw new Error(`Market contributors failed (${response.status}): ${errText}`);
			}

			return await response.json();
		} catch (error: any) {
			console.error('Error fetching market contributors:', error);
			return { success: false, error: error?.message || 'Error fetching market contributors' };
		}
	};

	const saveColumnMapping = async (
		carrierName: string,
		mappingName: string,
		pdfColumns: string[],
		excelColumns: string[],
		columnMapping: Record<string, string>,
		notes?: string
	) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}

		try {
			const token = await user.getIdToken();
			const response = await fetch(`${BASE_URL}/api/ocr/save-mapping`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
				body: JSON.stringify({
					carrier_name: carrierName,
					mapping_name: mappingName,
					pdf_columns: pdfColumns,
					excel_columns: excelColumns,
					column_mapping: columnMapping,
					notes: notes
				})
			});

			if (!response.ok) {
				throw new Error(`Save mapping failed with status: ${response.status}`);
			}

			const result = await response.json();
			return result;

		} catch (error) {
			console.error('Error saving column mapping:', error);
			return { success: false, message: 'Error saving column mapping' };
		}
	};

	const getCarrierMappings = async (carrierName?: string) => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}

		try {
			const token = await user.getIdToken();
			const url = carrierName
				? `${BASE_URL}/api/ocr/get-mappings?carrier_name=${encodeURIComponent(carrierName)}`
				: `${BASE_URL}/api/ocr/get-mappings`;

			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${token}`,
				},
			});

			if (!response.ok) {
				throw new Error(`Get mappings failed with status: ${response.status}`);
			}

			const result = await response.json();
			return result;

		} catch (error) {
			console.error('Error getting carrier mappings:', error);
			return { success: false, message: 'Error getting carrier mappings', mappings: [] };
		}
	};

	const fetchVacAttachment = async (href: string): Promise<{ blob: Blob; contentType: string } | null> => {
		if (!user) return null;
		try {
			const token = await user.getIdToken();
			const response = await fetch(`${BASE_URL}/api/vac/attachment?href=${encodeURIComponent(href)}`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			const blob = await response.blob();
			// OI returns e.g. "application/pdf;charset=UTF-8" — strip parameters so callers can match on the bare MIME type.
			const contentType = blob.type.split(';')[0].trim().toLowerCase();
			return { blob, contentType };
		} catch (error) {
			console.error('fetchVacAttachment error:', error);
			return null;
		}
	};

	const fetchVacAttachmentsZip = async (itemId: string | number): Promise<Blob | null> => {
		if (!user) return null;
		try {
			const token = await user.getIdToken();
			const response = await fetch(`${BASE_URL}/api/vac/receipt/${itemId}/attachments/zip`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			return await response.blob();
		} catch (error) {
			console.error('fetchVacAttachmentsZip error:', error);
			return null;
		}
	};

	const checkDuplicateTicket = async (ticketNumber: string): Promise<MainTableItem | null> => {
		if (!user) {
			console.log("No user, cannot make API call");
			return null;
		}

		try {
			const token = await user.getIdToken();
			const response = await fetch(`${API_URL.replace('/api/data', '/api/check-duplicate-ticket')}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Param': ticketNumber,
				},
			});

			if (!response.ok) {
				throw new Error(`Check duplicate failed with status: ${response.status}`);
			}

			const result = await response.json();

			// Check the new response format: {success: true, hasDuplicate: boolean, count: number}
			if (result.success && result.hasDuplicate) {
				// Return a minimal object to indicate duplicate found
				// The ticket_number is the only field we know for certain
				return {
					ticket_number: ticketNumber,
					count: result.count
				} as any as MainTableItem;
			}

			return null;

		} catch (error) {
			console.error('Error checking for duplicate ticket:', error);
			return null;
		}
	};

	const reportUploader = async (file: File, carrier_name: string): Promise<UploadResult | undefined> => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}

		if (!file) return;

		const formData = new FormData();
		formData.append('file', file);

		console.log('file uploaded')
		console.log(formData)
		try {
			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					// 'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'process_carrier_file',
					'Request-Value': carrier_name,
				},
				body: formData
			});

			if (!response.ok) {
				throw new Error(`Upload failed with status: ${response.status}`);
			}

			const result = await response.json();
			// setUploadStatus('File processed successfully!');
			// console.log('Processing result:', result);
			return result

		} catch (error) {
			console.error('Error processing file:', error);
			// setUploadStatus('Error processing file');
		}
	};

	const runRFPAnalysis = async (payload: RFPAnalysisPayload): Promise<RFPAnalysisResult> => {
		if (!user) throw new Error('Not authenticated');
		const token = await user.getIdToken();
		const response = await fetch(API_URL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${token}`,
				'Request-Platform': 'web',
				'X-Request-Type': 'rfp_analyze',
			},
			body: JSON.stringify(payload),
		});
		if (!response.ok) {
			const text = await response.text().catch(() => response.statusText);
			throw new Error(`RFP analysis failed (${response.status}): ${text}`);
		}
		return response.json() as Promise<RFPAnalysisResult>;
	};

	const fetchRFPLeaseStats = async (producerId: number | string): Promise<RFPLeaseStatItem[]> => {
		if (!user) throw new Error('Not authenticated');
		const token = await user.getIdToken();
		const response = await fetch(API_URL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${token}`,
				'Request-Platform': 'web',
				'X-Request-Type': 'rfp_lease_stats',
			},
			body: JSON.stringify({ producer_id: producerId }),
		});
		if (!response.ok) {
			const text = await response.text().catch(() => response.statusText);
			throw new Error(`Lease stats failed (${response.status}): ${text}`);
		}
		const json = await response.json() as { leases: RFPLeaseStatItem[] };
		return json.leases ?? [];
	};

	const pdfScraper = async () => {
		if (!user) {
			console.log("No user, cannot make API call");
			return;
		}

		console.log('pdf scraper init')

		try {
			const token = await user.getIdToken();
			const response = await fetch(`${API_URL}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
					'Request-Platform': 'web',
					'X-Request-Type': 'pdf_scraper',
				},
			});

			if (!response.ok) {
				throw new Error(`pdf scraper call failed with status: ${response.status}`);
			}

			// Parse the JSON response
			const data = await response.json();
			const processedCount = data.success;
			return processedCount;

		} catch (error) {
			console.error('An error occurred while sending pdf scraper call', error);
		}
	};

	// Memoize context value to prevent unnecessary re-renders when object reference changes
	// ── OAuth admin functions ─────────────────────────────────────────────────────

	const fetchOAuthClients = async (): Promise<OAuthClient[]> => {
		if (!user) throw new Error('Not authenticated');
		const token = await user.getIdToken();
		const res = await fetch(`${BASE_URL}/api/admin/oauth-clients`, {
			headers: { Authorization: `Bearer ${token}` },
		});
		const data = await res.json();
		if (!res.ok) throw new Error(data.error || 'Failed to load clients');
		// MySQL returns JSON columns as raw strings — parse allowed_ips here
		// so callers always receive string[] | null.
		const clients = (data.data as OAuthClient[]).map(c => ({
			...c,
			allowed_ips: typeof c.allowed_ips === 'string'
				? JSON.parse(c.allowed_ips as unknown as string)
				: c.allowed_ips,
		}));
		// Populate apiData.oauth_clients so socket edit_row / new_row handlers
		// have a valid array to merge into, and the panel's useEffect dependency fires.
		setApiData(prev => prev ? { ...prev, oauth_clients: clients } : prev);
		return clients;
	};

	const createOAuthClient = async (
		label: string, producer_id: number, scopes: string[], allowed_ips: string[] | null
	): Promise<{ client_id: string; client_secret: string; label: string }> => {
		if (!user) throw new Error('Not authenticated');
		const token = await user.getIdToken();
		const res = await fetch(`${BASE_URL}/api/admin/oauth-clients`, {
			method: 'POST',
			headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
			body: JSON.stringify({ label, producer_id, scopes, allowed_ips }),
		});
		const data = await res.json();
		if (!res.ok) throw new Error(data.error || 'Creation failed');
		return { client_id: data.client_id, client_secret: data.client_secret, label: data.label };
	};

	const toggleOAuthClient = async (clientId: string, isActive: boolean): Promise<void> => {
		if (!user) throw new Error('Not authenticated');
		const token = await user.getIdToken();
		const res = await fetch(`${BASE_URL}/api/admin/oauth-clients/${clientId}/toggle`, {
			method: 'POST',
			headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
			body: JSON.stringify({ is_active: isActive }),
		});
		if (!res.ok) {
			const data = await res.json();
			throw new Error(data.error || 'Toggle failed');
		}
	};

	const rotateOAuthClient = async (clientId: string): Promise<{ client_id: string; client_secret: string }> => {
		if (!user) throw new Error('Not authenticated');
		const token = await user.getIdToken();
		const res = await fetch(`${BASE_URL}/api/admin/oauth-clients/${clientId}/rotate`, {
			method: 'POST',
			headers: { Authorization: `Bearer ${token}` },
		});
		const data = await res.json();
		if (!res.ok) throw new Error(data.error || 'Rotate failed');
		return { client_id: data.client_id, client_secret: data.client_secret };
	};

	const updateOAuthClientIps = async (clientId: string, ips: string[] | null, oldIps: string[]): Promise<void> => {
		if (!user) throw new Error('Not authenticated');
		const token = await user.getIdToken();
		const res = await fetch(`${BASE_URL}/api/admin/oauth-clients/${clientId}/ips`, {
			method: 'POST',
			headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
			body: JSON.stringify({ allowed_ips: ips, old_allowed_ips: oldIps }),
		});
		const data = await res.json();
		if (!res.ok) throw new Error(data.error || 'Failed to update IPs');
	};

	const updateOAuthClientScopes = async (clientId: string, scopes: string[], oldScopes: string[]): Promise<void> => {
		if (!user) throw new Error('Not authenticated');
		const token = await user.getIdToken();
		const res = await fetch(`${BASE_URL}/api/admin/oauth-clients/${clientId}/scopes`, {
			method: 'PATCH',
			headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
			body: JSON.stringify({ scopes, old_scopes: oldScopes }),
		});
		const data = await res.json();
		if (!res.ok) throw new Error(data.error || 'Failed to update scopes');
	};

	const fetchOAuthClientLog = async (
		clientId: string, page: number, perPage: number
	): Promise<{ data: ApiLogEntry[]; total: number; page: number; per_page: number }> => {
		if (!user) throw new Error('Not authenticated');
		const token = await user.getIdToken();
		const res = await fetch(
			`${BASE_URL}/api/admin/oauth-clients/${clientId}/log?page=${page}&per_page=${perPage}`,
			{ headers: { Authorization: `Bearer ${token}` } }
		);
		const data = await res.json();
		if (!res.ok) throw new Error(data.error || 'Failed to load log');
		return data;
	};

	const contextValue = React.useMemo(() => ({
		apiData,
		setApiData,
		socketStatus: debouncedSocketStatus, // Use debounced status to prevent rapid changes
		setSocketStatus,
		apiHealthStatus,
		heartbeatStats,
		triggerManualHeartbeat,
		clientIP,
		clientInfo,
		dbInfo,
		userDetails,
		setUserDetails,
		allUsers,
		isUsersLoading,
		fetchAllUsers,
		isLoading,
		isEditLoading,
		isCarrierLoading,
		isCreatingLoading,
		handleDeleteRow,
		handleEditOrder,
		handleAddRow,
		getUserById,
		getUsersByIds,
		handleGetTable,
		handleMonthRequest,
		handleGetChangeLog,
		handleGetChangeLogTable,
		fetchRidAudit,
		handleEditSequence,
		handleEditReroute,
		TriggerDailyUpdate,
		TriggerCustomerReport,
		handleCreateUser,
		handleDeleteUser,
		handleResetPassword,
		fetchCarrierTable,
		fetchAutomateScheduling,
		isAutomateSchedulingLoading,
		pdfUploaded,
		reportUploader,
		pdfScraper,
		extractTableFromPdf,
		handleUploadLabPdf,
		handleCommitLabPdf,
		handleGetMarketContributors,
		saveColumnMapping,
		getCarrierMappings,
		checkDuplicateTicket,
		fetchVacAttachment,
		fetchVacAttachmentsZip,
		processingProgress,
		rowLocks,
		finishedJobs,
		runRFPAnalysis,
		fetchRFPLeaseStats,
		fetchOAuthClients,
		createOAuthClient,
		toggleOAuthClient,
		rotateOAuthClient,
		updateOAuthClientIps,
		updateOAuthClientScopes,
		fetchOAuthClientLog,
	}), [
		apiData,
		debouncedSocketStatus,
		apiHealthStatus,
		heartbeatStats,
		triggerManualHeartbeat,
		clientIP,
		clientInfo,
		dbInfo,
		userDetails,
		allUsers,
		isUsersLoading,
		isLoading,
		isEditLoading,
		isCarrierLoading,
		isCreatingLoading,
		isAutomateSchedulingLoading,
		processingProgress,
		rowLocks,
		finishedJobs,
		// Functions are excluded from dependencies as they should be stable
	]);

	return (
		<UserInfoContext.Provider value={contextValue}>
			{children}
		</UserInfoContext.Provider>
	);
};

export const useApiData = (): UserInfoContextType => {
	const context = useContext(UserInfoContext);
	if (!context) {
		throw new Error('useApiData must be used within a UserInfoProvider');
	}
	return context;
};
